import { KpiTint } from "@/shell/components/KpiCard";
import { DeltaPolarity } from "@/shell/components/Delta";
import { CohortRow } from "@/shell/components/CohortTriangle";
import {
    CustomerIntelligenceResponse,
    OrdersCohortsResponse,
    OrdersCustomersResponse,
} from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

export type CustomerKpiVM = {
    icon: string;
    label: string;
    value: string;
    sub: string;
    mom: number | null;
    unit?: "%" | "pp";
    tint: KpiTint;
    deltaPolarity?: DeltaPolarity;
};

export type ConcentrationTileVM = {
    label: string;
    value: string;
    sub: string;
    tint: "default" | "critical" | "ok";
};

export type CohortFooterVM = {
    avgSize: string;
    avgM3: string;
    avgM6: string;
    avgM12: string;
};

export type CustomersVM = {
    customerBase: CustomerKpiVM[];
    customerValue: CustomerKpiVM[];
    cohortRows: CohortRow[];
    monthsTracked: number;
    cohortFooter: CohortFooterVM;
    concentration: ConcentrationTileVM[];
};

const MUTED_VALUE = "—";
const MUTED_SUB = "Sin datos disponibles";

type FromDataInput = {
    customers: OrdersCustomersResponse | null;
    cohorts: OrdersCohortsResponse | null;
    customerIntelligence: CustomerIntelligenceResponse | null;
};

const emptyKpi = (icon: string, label: string, tint: KpiTint = "muted"): CustomerKpiVM => ({
    icon,
    label,
    value: MUTED_VALUE,
    sub: MUTED_SUB,
    mom: null,
    tint,
});

const averageAtMonth = (rows: CohortRow[], month: number): string => {
    // row.values[month] is undefined when a cohort has fewer tracked months than
    // `month` (e.g. M6/M12 on a short window). Guard against undefined/NaN so the
    // footer never renders a fabricated "NaN%" — no finite value means no data.
    const values = rows
        .map((row) => row.values[month])
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    if (values.length === 0) return MUTED_VALUE;
    const average = values.reduce((sum, value) => sum + value, 0) / values.length;
    return `${average.toFixed(0)}%`;
};

const fromData = (input: FromDataInput): CustomersVM => {
    const { customers, cohorts, customerIntelligence } = input;

    const customerBase: CustomerKpiVM[] = [
        customers === null
            ? emptyKpi("groups", "Active workspaces", "primary")
            : {
                  icon: "groups",
                  label: "Active workspaces",
                  value: formatters.formatNumber(customers.activeUniqueUsers),
                  sub: "current period",
                  mom: null,
                  tint: "primary",
              },
        customers === null
            ? emptyKpi("person_add", "New workspaces", "primary")
            : {
                  icon: "person_add",
                  label: "New workspaces",
                  value: formatters.formatNumber(customers.newUsers),
                  sub: "current period",
                  mom: null,
                  tint: "primary",
              },
        customers === null
            ? emptyKpi("autorenew", "Retained", "primary")
            : {
                  icon: "autorenew",
                  label: "Retained",
                  value: formatters.formatNumber(customers.recurringUsers),
                  sub: "current period",
                  mom: null,
                  tint: "primary",
              },
        emptyKpi("person_remove", "Churned", "warning"),
        customers === null
            ? emptyKpi("repeat", "Repeat rate", "success")
            : {
                  icon: "repeat",
                  label: "Repeat rate",
                  value: `${formatters.formatNumber(customers.repeatRate)}%`,
                  sub: "current period",
                  mom: null,
                  tint: "success",
              },
    ];

    const customerValue: CustomerKpiVM[] = [
        customers === null
            ? emptyKpi("payments", "Revenue / workspace", "violet")
            : {
                  icon: "payments",
                  label: "Revenue / workspace",
                  value: formatters.formatCurrency(customers.businessRevenuePerUser),
                  sub: "current period",
                  mom: null,
                  tint: "violet",
              },
        customers === null
            ? emptyKpi("currency_exchange", "Volume / workspace", "violet")
            : {
                  icon: "currency_exchange",
                  label: "Volume / workspace",
                  value: formatters.formatCurrency(customers.volumePerUser),
                  sub: "current period",
                  mom: null,
                  tint: "violet",
              },
        customers === null
            ? emptyKpi("shopping_cart", "Orders / workspace", "violet")
            : {
                  icon: "shopping_cart",
                  label: "Orders / workspace",
                  value: formatters.formatNumber(customers.ordersPerUser),
                  sub: "current period",
                  mom: null,
                  tint: "violet",
              },
        emptyKpi("receipt_long", "ARPU LTM", "violet"),
    ];

    const cohortRows: CohortRow[] = (cohorts?.rows ?? []).map((row) => ({
        cohort: row.cohortLabel,
        values: row.retention,
    }));

    const tops = customerIntelligence?.valueConcentration.tops ?? [];
    const findTop = (topCount: number): number | null =>
        tops.find((top) => top.topCount === topCount)?.revenueShare ?? null;

    const top1 = findTop(1);
    const top3 = findTop(3);
    const top5 = findTop(5);

    const concentration: ConcentrationTileVM[] = [
        {
            label: "Top client",
            value: top1 === null ? MUTED_VALUE : `${top1.toFixed(1)}%`,
            sub: top1 === null ? MUTED_SUB : "revenue share",
            tint: top1 === null ? "default" : "critical",
        },
        {
            label: "Top 3",
            value: top3 === null ? MUTED_VALUE : `${top3.toFixed(1)}%`,
            sub: top3 === null ? MUTED_SUB : "revenue share",
            tint: top3 === null ? "default" : "critical",
        },
        {
            label: "Top 5",
            value: top5 === null ? MUTED_VALUE : `${top5.toFixed(1)}%`,
            sub: top5 === null ? MUTED_SUB : "revenue share",
            tint: top5 === null ? "default" : "critical",
        },
        { label: "Non-top growth", value: MUTED_VALUE, sub: MUTED_SUB, tint: "default" },
        { label: "Revenue ex-top", value: MUTED_VALUE, sub: MUTED_SUB, tint: "default" },
    ];

    return {
        customerBase,
        customerValue,
        cohortRows,
        monthsTracked: cohorts?.monthsTracked ?? 12,
        cohortFooter: {
            avgSize:
                cohortRows.length === 0
                    ? MUTED_VALUE
                    : `${Math.round(
                          (cohorts?.rows ?? []).reduce((sum, row) => sum + row.size, 0) / cohortRows.length
                      )} workspaces/mo`,
            avgM3: averageAtMonth(cohortRows, 3),
            avgM6: averageAtMonth(cohortRows, 6),
            avgM12: averageAtMonth(cohortRows, 12),
        },
        concentration,
    };
};

export const customersViewModel = { fromData };
